import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { articleLd } from "@/lib/seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowLeft } from "lucide-react";

export default function ArticleDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div data-testid="article-detail-page">
      {article && (
        <Seo
          title={article.title}
          description={article.excerpt}
          path={`/insights/${article.slug}`}
          image={article.cover_image || undefined}
          type="article"
          jsonLd={articleLd(article)}
        />
      )}
      <Header />
      <main className="pt-36 pb-32 px-6 md:px-12 min-h-screen">
        <div className="max-w-3xl mx-auto">
          <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 hover:text-gold transition-colors mb-12">
            <ArrowLeft size={14} /> All Insights
          </Link>

          {notFound ? (
            <p className="text-muted2 font-serif text-3xl" data-testid="article-not-found">Article not found.</p>
          ) : !article ? (
            <p className="text-muted2 font-mono text-sm">Loading…</p>
          ) : (
            <article>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{article.category}</p>
              <h1 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-[1.05] mb-8" data-testid="article-title">
                {article.title}
              </h1>
              <p className="text-muted2 text-lg leading-relaxed mb-10 border-l border-gold/40 pl-6 italic font-serif">
                {article.excerpt}
              </p>
              {article.cover_image && (
                <img src={article.cover_image} alt={article.title} className="w-full aspect-[16/9] object-cover mb-12 grayscale" />
              )}
              {article.content && /<[a-z][\s\S]*>/i.test(article.content) ? (
                <div className="article-body text-bone/85 text-lg leading-relaxed font-light" data-testid="article-content" dangerouslySetInnerHTML={{ __html: article.content }} />
              ) : (
                <div className="article-body text-bone/85 text-lg leading-relaxed font-light" data-testid="article-content">
                  {article.content.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
              <div className="mt-16 pt-8 border-t border-white/15 flex items-center justify-between">
                <span className="font-mono text-xs text-muted2 tracking-[0.2em] uppercase">— {article.author}</span>
                <button onClick={() => { nav("/"); setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 120); }}
                  data-testid="article-cta" className="font-sans text-xs uppercase tracking-[0.2em] border border-gold/60 text-gold px-6 py-3 hover:bg-gold hover:text-ink transition-colors">
                  Book a Consultation
                </button>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
